import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import type { ChatEntry } from '../stores/chat'
import { ChatPanel } from './ChatPanel'

export interface ChatDockProps {
    readonly messages: readonly ChatEntry[]
    readonly unread: number
    readonly disabled?: boolean
    readonly taunts?: readonly string[]
    readonly onSend: (text: string) => void
    readonly onMarkRead?: () => void
}

/**
 * 聊天区的**摆放层**（内容层是 `ChatPanel`，两者刻意分开）：
 *
 * - `≥ md`(768px)：右侧固定栏，左游戏右聊天，两栏并排、互不侵占；
 * - `< md`：右下角悬浮按钮（有未读时带数量角标），点开是**底部半屏抽屉**（带顺滑展开动画），
 *   点「▾」或 Escape 收起。抽屉只盖下半屏，上半屏仍能看见棋盘。
 *
 * 关键点：聊天**绝不参与对流布局**。桌面端是并排的固定栏（高度由面板撑满），
 * 移动端是覆盖层而不是流内元素 —— 所以棋盘再也不会被聊天挤走或与之重叠。
 */
const DESKTOP_QUERY = '(min-width: 768px)'

export function ChatDock({
    messages,
    unread,
    disabled = false,
    taunts,
    onSend,
    onMarkRead,
}: ChatDockProps): ReactElement {
    const [desktop, setDesktop] = useState(false)
    const [open, setOpen] = useState(false)
    const [keyboardInset, setKeyboardInset] = useState(0)
    const markReadRef = useRef(onMarkRead)

    useEffect(() => {
        markReadRef.current = onMarkRead
    })

    /**
     * 软键盘弹出时把抽屉顶到键盘上方。
     * iOS 不会改变布局视口（`window.innerHeight` 不变），所以只能靠 visualViewport 判断；
     * 不处理的话输入框会被键盘整个盖住，对聊天框来说是致命的。
     */
    useEffect(() => {
        const viewport = window.visualViewport
        if (!viewport) return
        const sync = () => {
            const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
            setKeyboardInset(Math.round(inset))
        }
        sync()
        viewport.addEventListener('resize', sync)
        viewport.addEventListener('scroll', sync)
        return () => {
            viewport.removeEventListener('resize', sync)
            viewport.removeEventListener('scroll', sync)
        }
    }, [])

    useEffect(() => {
        const query = window.matchMedia(DESKTOP_QUERY)
        const sync = () => {
            setDesktop(query.matches)
            // 切到桌面布局时必须收起覆盖层，否则它会盖在并排布局上面
            if (query.matches) setOpen(false)
        }
        sync()
        query.addEventListener('change', sync)
        return () => query.removeEventListener('change', sync)
    }, [])

    // 只有「桌面端常显」或「覆盖层已打开」才算已读 —— 否则未读角标永远不出现
    useEffect(() => {
        if (desktop || open) markReadRef.current?.()
    }, [desktop, open, messages.length])

    useEffect(() => {
        if (!open) return
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [open])

    if (desktop) {
        return (
            <aside
                aria-label="聊天室"
                className="flex min-h-0 w-80 shrink-0 flex-col overflow-hidden rounded-[10px] bg-black/20"
            >
                <h2 className="shrink-0 border-b border-white/10 px-3 py-2 text-sm font-semibold text-white/80">
                    聊天室
                </h2>
                <ChatPanel messages={messages} disabled={disabled} taunts={taunts} onSend={onSend} />
            </aside>
        )
    }

    return (
        <>
            <button
                type="button"
                aria-label={unread > 0 ? `打开聊天室，${unread} 条未读` : '打开聊天室'}
                className="fixed right-[max(1rem,env(safe-area-inset-right))] bottom-[max(1rem,env(safe-area-inset-bottom))] z-40 flex h-14 w-14 items-center justify-center rounded-full border border-white/20 bg-[#2f333a] text-2xl shadow-[0_6px_20px_rgba(0,0,0,0.55)]"
                onClick={() => setOpen(true)}
            >
                💬
                {unread > 0 ? (
                    <span className="absolute -top-1 -right-1 min-w-[20px] rounded-full bg-danger px-1 text-center text-[0.7rem] leading-[20px] text-white">
                        {unread > 9 ? '9+' : unread}
                    </span>
                ) : null}
            </button>

            {/*
             * 底部半屏抽屉：始终挂载、用 transform 展开/收起。
             * 条件渲染只能做进场动画，收起时会“曝”地消失，所以这里靠 translate 切换。
             * 收起时加 inert + pointer-events-none，避免它仍然可聚焦/可点击。
             */}
            <section
                aria-label="聊天室"
                aria-hidden={!open}
                inert={!open}
                style={{ bottom: keyboardInset }}
                className={`fixed inset-x-0 z-50 flex h-[50dvh] flex-col overflow-hidden rounded-t-2xl border-t border-white/15 bg-[#23262c] pb-[env(safe-area-inset-bottom)] shadow-[0_-10px_30px_rgba(0,0,0,0.55)] transition-transform duration-300 ease-out ${open ? 'translate-y-0' : 'pointer-events-none translate-y-full'
                    }`}
            >
                <div className="flex shrink-0 justify-center pt-2">
                    <span aria-hidden="true" className="h-1 w-10 rounded-full bg-white/25" />
                </div>
                <header className="flex shrink-0 items-center justify-between gap-2 px-3 py-1.5">
                    <span className="text-sm font-semibold text-white/85">聊天室</span>
                    <button
                        type="button"
                        aria-label="收起聊天室"
                        className="min-h-10 rounded-lg border border-white/25 px-3 text-sm text-white/90 hover:bg-white/10"
                        onClick={() => setOpen(false)}
                    >
                        ▾
                    </button>
                </header>
                <ChatPanel messages={messages} disabled={disabled} taunts={taunts} onSend={onSend} />
            </section>
        </>
    )
}
